import React from "react";
import { useHistory } from "react-router-dom";
import { ArrowRight } from "lucide-react";

type PartnerSectionProps = {
  eyebrow: string;
  title: string;
  description: string;
  tags: string[];
  image: string;
  imageAlt: string;
  /** Work and Stay put the photo on the left; Play puts it on the right */
  imageSide?: "left" | "right";
};

/** "List your space" block: photo on one side, pitch and CTA on the other. */
export function PartnerSection({
  eyebrow,
  title,
  description,
  tags,
  image,
  imageAlt,
  imageSide = "left",
}: PartnerSectionProps) {
  const history = useHistory();

  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <img
          src={image}
          alt={imageAlt}
          className={`h-[360px] w-full rounded-3xl object-cover sm:h-[492px] ${
            imageSide === "right" ? "lg:col-start-2 lg:row-start-1" : ""
          }`}
        />
        <div className={imageSide === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
            {title}
          </h2>
          <p className="pt-[20px] text-[18px] leading-[29.25px] text-[#717182]">{description}</p>
          <div className="flex flex-wrap gap-2 pt-8">
            {tags.map(tag => (
              <span
                key={tag}
                className="rounded-full border-[1px] border-solid border-[#e5e7eb] px-[12px] py-1.5 text-[14px] leading-5 text-[#717182]"
              >
                {tag}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={() => history.push("/partner-signup")}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white drop-shadow-[0_2px_8px_rgba(1,123,239,0.2)] transition-colors hover:bg-[#0062c0]"
          >
            List Your Space
            <ArrowRight className="size-[15px]" />
          </button>
        </div>
      </div>
    </section>
  );
}

type Cta = { label: string; onClick: () => void };

type FinalCtaCardProps = {
  eyebrow: string;
  title: string;
  description: string;
  primaryCta: Cta;
  secondaryCta: Cta;
  /** Extra content between the description and the buttons (Move's pillar pills) */
  children?: React.ReactNode;
};

/** Closing white card on a tinted background. */
export function FinalCtaCard({ eyebrow, title, description, primaryCta, secondaryCta, children }: FinalCtaCardProps) {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <div className="flex flex-col items-center rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white px-6 py-12 text-center drop-shadow-[0_4px_16px_rgba(0,0,0,0.04)] sm:px-12">
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            {title}
          </h2>
          <p className="max-w-[576px] pt-[20px] text-[18px] leading-[29.25px] text-[#717182]">{description}</p>
          {children}
          <div className="flex flex-wrap justify-center gap-[16px] pt-8">
            <button
              type="button"
              onClick={primaryCta.onClick}
              className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-8 py-[16px] text-[16px] font-bold leading-6 text-white drop-shadow-[0_2px_8px_rgba(1,123,239,0.2)] transition-colors hover:bg-[#0062c0]"
            >
              {primaryCta.label}
              <ArrowRight className="size-[18px]" />
            </button>
            <button
              type="button"
              onClick={secondaryCta.onClick}
              className="rounded-full border-[1px] border-solid border-[#e5e7eb] px-8 py-[16px] text-[16px] font-semibold leading-6 text-[#0a0a0a] transition-colors hover:bg-gray-50"
            >
              {secondaryCta.label}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
